import React, { useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, getYear, getQuarter, addWeeks, addMonths, addQuarters, addYears } from 'date-fns';
import { cn } from '../utils/cn';
interface DateOption {
  label: string;
  startDate: Date;
  endDate: Date;
}
interface DateRangePickerProps {
  onSelect: (dates: {
    currentStartDate: Date;
    currentEndDate: Date;
    previousStartDate: Date;
    previousEndDate: Date;
  }) => void;
  onClose: () => void;
}
const DateRangePicker: React.FC<DateRangePickerProps> = ({
  onSelect,
  onClose
}) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('weekly');
  const [isCustom, setIsCustom] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<Date>();
  const [customEndDate, setCustomEndDate] = useState<Date>();
  const [hoveredTimeframe, setHoveredTimeframe] = useState<string>('weekly');
  const now = new Date();
  const currentYear = getYear(now);
  const startingPoint = new Date(2024, 2, 1); // March 2024
  const formatTimeframeLabel = (date: Date, type: string): string => {
    const year = getYear(date);
    switch (type) {
      case 'weekly':
        return `Starting ${format(date, 'MMMM d, yyyy')}`;
      case 'monthly':
        return format(date, 'MMMM yyyy');
      case 'quarterly':
        return `Q${getQuarter(date)} ${year}`;
      case 'yearly':
        return `${year}`;
      default:
        return format(date, 'MMM d, yyyy');
    }
  };
  const getTimeframeOptions = (timeframe: string): DateOption[] => {
    const now = new Date();
    const startingPoint = new Date(2024, 2, 1); // March 2024
    let current: Date;
    let dates: Date[] = [];
    switch (timeframe) {
      case 'yearly':
        // Start from current year and include 2024
        current = startOfYear(now);
        while (current.getFullYear() >= 2024) {
          if (current.getFullYear() === 2024) {
            // For 2024, use March 1st as the start date
            dates.push(startingPoint);
          } else {
            dates.push(current);
          }
          current = addYears(current, -1);
        }
        break;
      case 'weekly':
        current = startOfWeek(now, {
          weekStartsOn: 1
        });
        while (current >= startingPoint) {
          dates.push(current);
          current = addWeeks(current, -1);
        }
        break;
      case 'monthly':
        current = startOfMonth(now);
        while (current >= startingPoint) {
          dates.push(current);
          current = addMonths(current, -1);
        }
        break;
      case 'quarterly':
        current = startOfQuarter(now);
        while (current >= startingPoint) {
          dates.push(current);
          current = addQuarters(current, -1);
        }
        break;
      default:
        return [];
    }
    return dates.filter(date => date >= startingPoint).map(date => {
      let startDate: Date;
      let endDate: Date;
      switch (timeframe) {
        case 'yearly':
          if (date.getFullYear() === 2024) {
            // For 2024, start from March 1st
            startDate = startingPoint;
            endDate = endOfYear(date);
          } else {
            startDate = startOfYear(date);
            endDate = endOfYear(date);
          }
          break;
        case 'weekly':
          startDate = startOfWeek(date, {
            weekStartsOn: 1
          });
          endDate = endOfWeek(date, {
            weekStartsOn: 1
          });
          break;
        case 'monthly':
          startDate = startOfMonth(date);
          endDate = endOfMonth(date);
          break;
        case 'quarterly':
          startDate = startOfQuarter(date);
          endDate = endOfQuarter(date);
          break;
        default:
          startDate = date;
          endDate = date;
      }
      return {
        label: formatTimeframeLabel(startDate, timeframe),
        startDate,
        endDate
      };
    });
  };
  const handleDateOptionSelect = (option: DateOption) => {
    const periodLength = option.endDate.getTime() - option.startDate.getTime();
    const previousStartDate = new Date(option.startDate.getTime() - periodLength);
    const previousEndDate = new Date(option.endDate.getTime() - periodLength);
    onSelect({
      currentStartDate: option.startDate,
      currentEndDate: option.endDate,
      previousStartDate,
      previousEndDate
    });
    onClose();
  };
  const handleCustomDateSelect = (date: Date) => {
    if (!customStartDate) {
      setCustomStartDate(date);
    } else if (!customEndDate && date >= customStartDate) {
      setCustomEndDate(date);
      const periodLength = date.getTime() - customStartDate.getTime();
      const previousStartDate = new Date(customStartDate.getTime() - periodLength);
      const previousEndDate = new Date(date.getTime() - periodLength);
      onSelect({
        currentStartDate: customStartDate,
        currentEndDate: date,
        previousStartDate,
        previousEndDate
      });
      onClose();
    } else {
      setCustomStartDate(date);
      setCustomEndDate(undefined);
    }
  };
  const timeframeOptions = [{
    id: 'weekly',
    label: 'Weekly'
  }, {
    id: 'monthly',
    label: 'Monthly'
  }, {
    id: 'quarterly',
    label: 'Quarterly'
  }, {
    id: 'yearly',
    label: 'Yearly'
  }, {
    id: 'custom',
    label: 'Custom'
  }];
  return <div className="flex bg-white rounded-lg shadow-lg border divide-x" data-id="element-69">
      {/* Left Column */}
      <div className="w-40" data-id="element-70">
        {timeframeOptions.map(option => <button key={option.id} className={cn('w-full px-4 py-2 text-left hover:bg-gray-100 text-sm', selectedTimeframe === option.id && 'bg-gray-100')} onClick={() => {
        setSelectedTimeframe(option.id);
        setIsCustom(option.id === 'custom');
      }} onMouseEnter={() => setHoveredTimeframe(option.id)} data-id="element-71">
            {option.label}
          </button>)}
      </div>
      {/* Right Column */}
      <div className="w-72 max-h-[320px] overflow-y-auto" data-id="element-72">
        {isCustom ? <div className="p-2" data-id="element-73">
            <Calendar mode="single" selected={customEndDate || customStartDate} onSelect={date => date && handleCustomDateSelect(date)} disabled={date => date < startingPoint} initialFocus data-id="element-74" />
            <div className="text-xs text-gray-500 mt-2" data-id="element-75">
              {customStartDate ? customEndDate ? `${format(customStartDate, 'MMM d, yyyy')} - ${format(customEndDate, 'MMM d, yyyy')}` : 'Select end date' : 'Select start date'}
            </div>
          </div> : <div className="space-y-1 p-2" data-id="element-76">
            {getTimeframeOptions(selectedTimeframe).map((option, index) => <button key={index} className="w-full px-3 py-2 text-left hover:bg-gray-100 rounded-md text-sm" onClick={() => handleDateOptionSelect(option)} data-id="element-77">
                {option.label}
              </button>)}
          </div>}
      </div>
    </div>;
};
export default DateRangePicker;